import React, { useState, useMemo, useEffect } from "react";
import { Box, Button, Grid2, Rating, Typography, FormLabel, TextField } from "@mui/material";
import { ReactComponent as RatingIcon } from "Assets/Icons/Classroom/RatingIcon.svg";
import { ReactComponent as FileIcon } from "Assets/Icons/Classroom/FileIcon.svg";
import { ReactComponent as SuccessIcon } from "Assets/Icons/Settings/SuccessIcon.svg";
import PrimaryIcon from "Components/Common/PrimaryIcon";
import { submitSessionFeedback } from "Slices/thunks";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

const styles = (theme) => ({
  "& .rating": {
    border: `1px solid ${theme.palette.border.blue}`,
    borderRadius: "8px",
    padding: "50px",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: "16px",
    "& .ratingStars": {
      "& .rate": {
        border: `1px solid ${theme.palette.border.blue}`,
        borderRadius: "8px",
        padding: "8px 13px",
        transition: "background 0.3s ease",
        "& svg": {
          fill: theme.palette.background.default,
          transition: "fill 0.3s ease", // smooth star fill
        },
        "&:hover": {
          background: theme.palette.primary.main,
          cursor: "pointer",
          "& svg": {
            fill: theme.palette.warning.main,
            transition: "fill 0.3s ease",
          },
        },
      },
    },
    "& .selected": {
      background: theme.palette.primary.main,
      cursor: "pointer",
      "& svg": {
        fill: `${theme.palette.warning.main} !important`,
      },
      
    }
  },
});

const initialValues = {
  RatingID: "",
  GoodToKnowIDs: [],
  GoodToKnowSuggestion: "",
  ImproveIDs: [],
  ImproveSuggestion: "",
  FeedbackSuggestion: ""
}

const FeedbackTab = ({ session }) => {
  const { id = 0, sessionId = 0, tab } = useParams();
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(session.FeedbackID ? 5 : 1);
  const [hoverValue, setHoverValue] = useState(null);
  let rates = {
    1: "Poor",
    3: "Average",
    5: "Excellent",
  };

  const { Common, Translation } = useSelector((state) => ({
    Common: state.Common.common || {},
    Translation: state.Translation.translation?.Course || {},
  }));
  
  const Schema = useMemo(() => {
    return yup.object({
      RatingID: yup.string().required(Translation?.RatingRequired || "Rating is required."),
      GoodToKnowIDs: yup.array(),
      GoodToKnowSuggestion: yup.string(),
      ImproveIDs: yup.array().required(Translation?.ImproveSuggestionRequired || "Improve suggestion is required."),
      ImproveSuggestion: yup.string(),
      FeedbackSuggestion: yup.string()
    });
  }, [Translation]) 
  
  const {
    values,
    setValues,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    setErrors,
    resetForm,
    touched,
    submitCount,
    setTouched,
    setFieldValue,
    setFieldTouched,
    validateForm
  } = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: (data) => {
      console.log("data", data);
      const feedbackObj = {
        ...data,
        SessionID: session.ID
      }
      dispatch(submitSessionFeedback(feedbackObj));
    },
    validationSchema: Schema,
  });

  const feedbacks = useMemo(() => {
    return [
      {
        color: "#FEE0CC",
        icon: FileIcon,
        label: Translation?.FeedbackLabel1,
        desc: Translation?.FeedbackDescription1
      },
      {
        icon: FileIcon,
        color: "#D4F7ED",
        label: Translation?.FeedbackLabel2,
        desc: Translation?.FeedbackDescription2
      },
      {
        icon: FileIcon,
        color: "#E2DBFA",
        label: Translation?.FeedbackLabel3,
        desc: Translation?.FeedbackDescription3
      },
    ];
  }, [Translation])


  const handleClick = (name, value) => {
    let updatedValues;
    if (values[name]?.includes(value)) {
      updatedValues = values[name].filter((item) => item != value);
      setFieldValue(name, updatedValues)
    } else {
      updatedValues = [...values[name], value]
    }
    setFieldValue(name, updatedValues)
  }


  const handleNext = async () => {
    const errors = await validateForm(); // validate manually

    if (currentTab === 1 && !errors?.RatingID) {
      values.RatingID ?
        setCurrentTab(currentTab + 1) :
        setErrors({RatingID: Translation.RatingRequired})
    } else if (currentTab === 2) {
      setCurrentTab(currentTab + 1);
    } else if (currentTab === 3) {
      values.ImproveIDs.length ?
      setCurrentTab(currentTab + 1) : 
      setErrors({ImproveIDs: Translation?.ImproveSuggestionRequired})
    }
  }

  useEffect(() => {
    resetForm();
    setValues(initialValues);
    session.FeedbackID ?
      setCurrentTab(5) :
      setCurrentTab(1)

  }, [sessionId])

  return (
    <form 
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}>
      <Grid2 container spacing={2} sx={styles}>
        { currentTab === 1 ?
          <Grid2 className="rating" size={5}>
            <FormLabel color="secondary" fontWeight={500} sx={{ fontSize: "18px" }} label>
              {`${currentTab}. ${Translation?.PleaseRateInstructor}`}
            </FormLabel>
            <Box mb="10px">
              <Box display={"flex"} gap="16px" className="ratingStars">
                {(Common?.Rating || []).map((item, i) => {
                  const isSelected = item.Value2 <= hoverValue || item.Value2 <= values.RatingID;
                  return (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"center"}
                      gap="10px"
                      onMouseEnter={() => setHoverValue(item.Value2)}
                      onMouseLeave={() => setHoverValue(null)}
                      onClick={() => setFieldValue('RatingID', item.Value2)}
                    >
                      <Box className={`rate ${isSelected ? "selected" : ""}`}>
                        <PrimaryIcon
                          width="24px"
                          height="24px"
                          icon={RatingIcon}
                        />
                      </Box>
                      <Typography>{rates[item.Value2]}</Typography>
                    </Box>
                  );
                })}
              </Box>
              {(touched.RatingID || submitCount > 0) && errors.RatingID && (
                <Typography color="error" variant="body2" mt={1}>
                  {errors.RatingID}
                </Typography>
              )}
            </Box>
            <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} width="100%">
              {/* <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={() => setCurrentTab(currentTab - 1)}
              >
                {Translation?.Back}
              </Button> */}
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setFieldTouched("RatingID", true);
                  handleNext();
                }}
              >
                {Translation?.Next}
              </Button>
            </Box>
          </Grid2> : 
          currentTab === 2 ?
          <Grid2 className="rating" size={5}>
            <Box display={"flex"} flexDirection={"column"}>
              <Typography textAlign={"left"} variant="title" fontWeight={500}>
                {`${currentTab}. ${Translation?.GoodToKnow}`}
              </Typography>
              <FormLabel color="secondary" fontWeight={500} sx={{ fontSize: "18px" }} label>
                {Translation?.WhatAspectProduct}
              </FormLabel>
            </Box>
            <Box mb="10px" width="80%">
              <Box display={"flex"} flexDirection={"column"} alignItems={"left"}  gap="10px">
                {(Common?.GoodToKnow || []).map((item, i) => {
                  return (
                    <Button color={values.GoodToKnowIDs.includes(item.ID) ? "primary" : "secondary"}
                    variant={values.GoodToKnowIDs.includes(item.ID) ? "contained" : "outlined"} onClick={() => handleClick('GoodToKnowIDs', item.ID)}>
                      {item.Value}
                    </Button>
                  );
                })}
                <TextField
                  size="small"
                  color="secondary"
                  name="GoodToKnowSuggestion"
                  value={values.GoodToKnowSuggestion}
                  placeholder={Translation?.Other}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
            </Box>
            <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} width="100%">
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={() => setCurrentTab(currentTab - 1)}
              >
                {Translation?.Back}
              </Button>
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={handleNext}
              >
                {Translation?.Next}
              </Button>
            </Box>
          </Grid2> : 
          currentTab === 3 ?
          <Grid2 className="rating" size={5}>
            <FormLabel textAlign={"left"} color="secondary" fontWeight={500} sx={{ fontSize: "18px" }} required label>
              {`${currentTab}. ${Translation?.WhatImproveUpcomingSession}`}
            </FormLabel>
            <Box mb="10px" width="80%">
              <Box display={"flex"} flexDirection={"column"} alignItems={"left"}  gap="10px">
                {(Common?.Improve || []).map((item, i) => {
                  return (
                    <Button color={values.ImproveIDs.includes(item.ID) ? "primary" : "secondary"}
                      variant={values.ImproveIDs.includes(item.ID) ? "contained" : "outlined"} onClick={() => handleClick('ImproveIDs', item.ID)}>
                      {item.Value}
                    </Button>
                  );
                })}
                <TextField
                  size="small"
                  color="primary"
                  name="ImproveSuggestion"
                  value={values.ImproveSuggestion}
                  placeholder={Translation?.Other}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
              {errors.ImproveIDs && (
                <Typography color="error" variant="body2" mt={1}>
                  {errors.ImproveIDs}
                </Typography>
              )}
            </Box>
            <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} width="100%">
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={() => setCurrentTab(currentTab - 1)}
              >
                {Translation?.Back}
              </Button>
              <Button
                sx={{ width: "fit-content" }}
                variant="outlined"
                color="secondary"
                onClick={handleNext}
              >
                {Translation?.Next}
              </Button>
            </Box>
          </Grid2> :
           currentTab === 4 ?
          <Grid2 className="rating" size={5}>
            <FormLabel textAlign={"left"} color="secondary" fontWeight={500} sx={{ fontSize: "18px" }} label>
              {`${currentTab}. ${Translation?.FeedbackSuggestion}`}
            </FormLabel>
            <Box mb="10px" width="100%">
              <Box display={"flex"} flexDirection={"column"} alignItems={"left"}  gap="10px">
                <TextField
                  size="small"
                  // color="primary"
                  name="FeedbackSuggestion"
                  value={values.FeedbackSuggestion}
                  placeholder={Translation?.TypeYourAnswer}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Box>
            </Box>
            <Button
              type="submit"
              sx={{ width: "fit-content" }}
              variant="outlined"
              color="secondary"
            >
              {Translation?.Submit}
            </Button>
          </Grid2> : 
          currentTab === 5 ?
          <Grid2 className="rating" size={5}>
            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
               <PrimaryIcon
                  width="80px"
                  height="80px"
                  icon={SuccessIcon}
                />
              <Typography variant="title" fontWeight={600} color="success">
                {Translation?.FeedbackSubmittedSuccessfully}
              </Typography>
              <Typography variant="subtitle" color="secondary" textAlign="center">
                {Translation?.ThankYouForYourFeedback}
              </Typography>
            </Box>
          </Grid2> : <></>
        }


        <Grid2 size={7} display={"flex"} flexDirection={"column"} gap="16px">
          {feedbacks?.map((item, i) => {
            return (
              <Box
                key={i}
                display={"flex"}
                flexDirection={"column"}
                gap="16px"
                sx={(theme) => ({
                  background: item?.color,
                  borderRadius: "8px",
                  padding: "16px",
                })}
              >
                <Typography
                  display={"flex"}
                  alignItems={"center"}
                  gap="8px"
                  variant="subtitle"
                  fontWeight={500}
                >
                  <PrimaryIcon icon={item?.icon} />
                  {item?.label}
                </Typography>
                <Typography>{item?.desc}</Typography>
              </Box>
            );
          })}
        </Grid2>
      </Grid2>
    </form>
  );
};

export default FeedbackTab;
